import React from 'react';
import {
    Table,
    TableColumn,
    TableRow,
    TableHeaderTheme,
    TableRowTheme,
    TableBorderRadius,
    TableBorderStyle,
    TableRowSeperatorStyle,
} from '@apply/components';
import { DemoSection } from './common/SectionNavigator';
import { DemoExample, DemoPage, DemoPageSection } from './common/DemoLayout';

const sections: DemoSection[] = [
    { id: 'tableDemo_tablewithheader', title: 'Table with Header' },
    { id: 'tableDemo_tablewithnoheader', title: 'Table with no Header' },
    { id: 'tableDemo_rowfontsize', title: 'Row Font Size' },
    { id: 'tableDemo_separatorexamples', title: 'Separator Examples' },
    { id: 'tableDemo_styleexamples', title: 'Style Examples' },
];

export const TableDemo: React.FC = () => {
    const htmlElement = () => {
        return (
            <div>
                <b>Editor</b>
            </div>
        );
    };

    const tableData: TableRow[] = [
        {
            Name: 'Alice Johnson',
            Email: 'alice@example.com',
            Role: 'Administrator',
        },
        {
            Name: 'Bob Smith',
            Email: 'bob@example.com',
            Role: 'User',
        },
        {
            Name: 'Charlie Lee',
            Email: 'charlie@example.com',
            Role: htmlElement(),
        },
    ];

    return (
        <DemoPage
            title='Tables :'
            sections={sections}
        >
            <DemoPageSection
                id={sections[0].id}
                title={sections[0].title}
            >
                <DemoExample>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection
                id={sections[1].id}
                title={sections[1].title}
            >
                <DemoExample>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        data={tableData}
                        width='100%'
                        showHeader={false}
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='30%'
                        />
                    </Table>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection
                id={sections[2].id}
                title={sections[2].title}
            >
                <DemoExample>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                        rowFontSize='12px'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection
                id={sections[3].id}
                title={sections[3].title}
            >
                <DemoExample>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                        rowSeperatorStyle={TableRowSeperatorStyle.None}
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>Small</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                        rowSeperatorStyle={TableRowSeperatorStyle.Small}
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>Medium</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                        rowSeperatorStyle={TableRowSeperatorStyle.Medium}
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>Large</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                        rowSeperatorStyle={TableRowSeperatorStyle.Large}
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
            </DemoPageSection>
            <DemoPageSection
                id={sections[4].id}
                title={sections[4].title}
            >
                <DemoExample>
                    <Table
                        headerTheme={TableHeaderTheme.White}
                        rowTheme={TableRowTheme.White}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>All Gray</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.Gray}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>All Red</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Red}
                        rowTheme={TableRowTheme.Red}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>All Blue</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Blue}
                        rowTheme={TableRowTheme.Blue}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>All Yellow</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Yellow}
                        rowTheme={TableRowTheme.Yellow}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>All Transparent</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Transparent}
                        rowTheme={TableRowTheme.Transparent}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>Blue</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Blue}
                        rowTheme={TableRowTheme.White}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>Red</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Red}
                        rowTheme={TableRowTheme.White}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>Gray</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Gray}
                        rowTheme={TableRowTheme.White}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
                <DemoExample label={<b>Yellow</b>}>
                    <Table
                        headerTheme={TableHeaderTheme.Yellow}
                        rowTheme={TableRowTheme.White}
                        borderRadius={TableBorderRadius.Small}
                        borderStyle={TableBorderStyle.Solid}
                        data={tableData}
                        width='100%'
                    >
                        <TableColumn
                            columnKey='Name'
                            header='Name'
                            width='30%'
                        />
                        <TableColumn
                            columnKey='Email'
                            header='Email'
                            width='40%'
                        />
                        <TableColumn
                            columnKey='Role'
                            header='Role'
                            width='20%'
                        />
                    </Table>
                </DemoExample>
            </DemoPageSection>
        </DemoPage>
    );
};
